import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import reviewAutomationService from '../../../utils/reviewAutomationService';
import customerService from '../../../utils/customerService'; // Import customerService
import { useNotification } from '../../../contexts/NotificationContext'; // Import useNotification

const ManualReviewButton = ({ customer, className = '' }) => {
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [canSend, setCanSend] = useState(false);
  const [disabledReason, setDisabledReason] = useState('');

  const checkEligibility = useCallback(async () => {
    if (!customer?.id) return;
    
    setLoading(true);
    try {
      const { canSend: isAbleToSend, reason } = await customerService.checkCanSendRequest(customer.id);
      setCanSend(isAbleToSend);
      setDisabledReason(isAbleToSend ? '' : reason);
    } catch (error) {
      console.error('Error checking review request eligibility:', error);
      setCanSend(false);
      setDisabledReason('Error checking eligibility.');
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  const handleSendRequest = async () => {
    setLoading(true);
    try {
      const result = await reviewAutomationService.sendManualReviewRequest(
        customer.id,
        null, // stageId not needed for manual requests
        customer.pipeline_stage || 'Manual Request'
      );

      if (result.success) {
        addNotification(`Review request sent to ${customer.email || customer.name}!`, 'success');
        // Refresh eligibility state after sending
        await checkEligibility();
      } else {
        addNotification(`Failed to send review request: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error sending manual review:', error);
      addNotification('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || !canSend || !customer.email;
  const tooltip = !customer.email 
    ? 'Customer has no email address' 
    : !canSend 
    ? disabledReason 
    : 'Send a new review request';

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleSendRequest}
      disabled={isDisabled}
      className={className}
      title={tooltip}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
          Processing...
        </>
      ) : (
        <>
          <Icon name="Star" size={16} className="mr-2" />
          Request Review
        </>
      )}
    </Button>
  );
};

export default ManualReviewButton; 